import { useEffect, useRef, useState } from "react";

import { createCancelSource } from "api/client";

const isPlainOptionsObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  typeof value !== "function";

// useApi(apiFunc)
// useApi(apiFunc, { cancelInflight: true })
// useApi(apiFunc, source, setSearchWithNDC)
// useApi(apiFunc, source, setSearchWithNDC, { cancelInflight: true })
const useApi = (apiFunc, sourceOrOptions, setSearchWithNDC, maybeOptions) => {
  const shortFormOptions =
    isPlainOptionsObject(sourceOrOptions) &&
    setSearchWithNDC === undefined &&
    maybeOptions === undefined
      ? sourceOrOptions
      : null;

  const source = shortFormOptions ? undefined : sourceOrOptions;
  const options = shortFormOptions || maybeOptions || {};
  const { cancelInflight = false } = options;

  const [data, setData] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  // Tracks the most recent in-flight request so that responses from older
  // calls (e.g. when filters are toggled quickly) are ignored. This prevents
  // the UI from showing stale data when a slower earlier request resolves
  // after a newer one.
  const requestIdRef = useRef(0);

  // When `cancelInflight` is enabled, holds the cancel source of the most
  // recent request so it can be aborted as soon as a new request is fired or
  // the component unmounts. Only used for idempotent reads — never enable
  // this for writes/mutations.
  const cancelSourceRef = useRef(null);

  const request = async (...args) => {
    let callArgs = args;

    if (cancelInflight) {
      cancelSourceRef.current?.cancel("superseded");
      const cancelSource = createCancelSource();
      cancelSourceRef.current = cancelSource;
      callArgs = [...args, { cancelToken: cancelSource.token }];
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    const response = await apiFunc(...callArgs);

    // A newer request was fired while we were awaiting; let it own the state.
    if (requestId !== requestIdRef.current) return;

    // The request was cancelled (e.g. on unmount with no successor). Don't
    // surface a fake error and don't toggle loading off — there's nothing
    // left to render against.
    if (response?.problem === "CANCEL_ERROR") return;

    setLoading(false);

    if (!response.ok) return setError(true);

    setError(false);
    if (source === "NDC" && response?.data?.response?.data?.length === 0)
      return setSearchWithNDC();
    else if (source === "NDC" && response?.data?.response?.data?.length > 0) {
      setSearchWithNDC("NDC");
      setData(response.data);
    } else {
      setData(response.data);
      if (setSearchWithNDC) {
        setSearchWithNDC("GDS");
      }
    }
  };

  useEffect(() => {
    return () => {
      if (cancelInflight) {
        cancelSourceRef.current?.cancel("unmount");
      }
    };
  }, [cancelInflight]);

  return { data, error, loading, request, setData };
};
export default useApi;
